import { useMemo } from 'react';
import clsx from 'clsx';

import { column } from '@/components/virtual-table';
import { itemTypes } from '@/constants/item-types';
import { ArchiveActionAlert } from '@/features/archive/archive-action-alert';
import { formatDate, getRandomItem } from '@/utils/helper-utils';
import type { SearchableDocument } from '@/services/search-service/orama-schema';

import { Button } from '@flintmint/web-shared/components/button';
import { Checkbox } from '@flintmint/web-shared/components/checkbox';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Skeleton } from '@flintmint/web-shared/components/skeleton';

import styles from '@/features/archive/archive.module.scss';

export type ArchiveColumnSelection = {
  selectedIds: ReadonlySet<string>;
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;
  toggleAllVisible: () => void;
  toggleItem: (item: SearchableDocument) => void;
};

type UseArchiveColumnsParams = {
  selection: ArchiveColumnSelection;
  onRestoreItem: (item: SearchableDocument) => Promise<unknown>;
  onDeleteItem: (item: SearchableDocument) => Promise<unknown>;
};

export const useArchiveColumns = ({ selection, onRestoreItem, onDeleteItem }: UseArchiveColumnsParams) => {
  const { selectedIds, allVisibleSelected, someVisibleSelected, toggleAllVisible, toggleItem } = selection;

  return useMemo(
    () => [
      column<SearchableDocument>({
        id: 'select',
        header: (
          <Checkbox
            className={styles.checkbox}
            checked={allVisibleSelected}
            intermediate={someVisibleSelected && !allVisibleSelected}
            onChange={toggleAllVisible}
          />
        ),
        width: 40,
        cell: (item) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox className={styles.checkbox} checked={selectedIds.has(item.id)} onChange={() => toggleItem(item)} />
          </div>
        ),
        skeleton: () => (
          <div className={styles.checkbox}>
            <Skeleton trueWidth={20} trueHeight={20} width={16} height={16} borderRadius={5} />
          </div>
        ),
      }),
      column<SearchableDocument>({
        id: 'name',
        header: 'Name',
        cell: (item) => (
          <div className={styles.nameCell}>
            <div className={styles.nameRow}>
              <Icon className={styles.nameIcon} type={itemTypes[item.type]?.icon || 'file-01'} size={20} />
              <div className={styles.name}>{item.name || 'Untitled'}</div>
            </div>
            {(item.parsedContent ?? item.content) && (
              <div className={clsx('multiline-ellipsis', styles.description)}>
                {(item.parsedContent ?? item.content).slice(0, 500)}
              </div>
            )}
          </div>
        ),
        skeleton: () => <ArchiveNameSkeleton />,
      }),
      column<SearchableDocument>({
        id: 'type',
        header: 'Type',
        width: 120,
        cell: (item) => <p className={styles.archiveCell}>{itemTypes[item.type]?.name || item.type}</p>,
        skeleton: () => <Skeleton height={6} borderRadius={4} width={64} />,
      }),
      column<SearchableDocument>({
        id: 'archived',
        header: 'Archived',
        width: 140,
        cell: (item) => <p className={styles.archiveCell}>{formatDate(item.archivedAt || item.updatedAt)}</p>,
        skeleton: () => <Skeleton height={6} borderRadius={4} width={72} />,
      }),
      column<SearchableDocument>({
        id: 'actions',
        header: 'Actions',
        width: 280,
        cell: (item) => <ArchiveRowActions onRestore={() => onRestoreItem(item)} onDelete={() => onDeleteItem(item)} />,
        skeleton: () => null,
      }),
    ],
    [allVisibleSelected, onDeleteItem, onRestoreItem, selectedIds, someVisibleSelected, toggleAllVisible, toggleItem]
  );
};

const ArchiveNameSkeleton = () => {
  const nameWidth = useMemo(() => getRandomItem([160, 118]), []);
  const descriptionWidth = useMemo(() => getRandomItem([210, 136]), []);

  return (
    <div className={styles.nameCell}>
      <div className={styles.nameRow}>
        <Skeleton trueWidth={20} trueHeight={20} width={16} height={16} borderRadius={5} />
        <Skeleton height={6} borderRadius={4} width={nameWidth} />
      </div>
      <Skeleton height={4} borderRadius={4} width={descriptionWidth} />
    </div>
  );
};

const ArchiveRowActions = ({ onRestore, onDelete }: { onRestore: () => void; onDelete: () => void }) => {
  return (
    <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
      <ArchiveActionAlert action="restore" count={1} onConfirm={onRestore} placement="bottom-end">
        <Button cornerRadius="smooth" color="hero" appearance="greyscale">
          Restore
        </Button>
      </ArchiveActionAlert>
      <ArchiveActionAlert action="delete" count={1} onConfirm={onDelete} placement="bottom-end">
        <Button cornerRadius="smooth" color="danger" appearance="greyscale">
          Delete from Archive
        </Button>
      </ArchiveActionAlert>
    </div>
  );
};
